import { View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import type { ReactElement } from 'react';

import type { Quote } from '@core/types/api/Quote/Quote';
import { randomUUID } from '@core/utils/uuid';

import { PdfHeader } from '../../../components/PdfHeader';
import { type PdfColumnConfig, PdfTable, pdfColumnFromConfig } from '../../../components/PdfTable';

const infoStyle: Style = {
  textAlign: 'left',
  paddingRight: '8pt',
};

type InfoRow = {
  id: string;
  value: string | null;
  viewStyle: Style;
};

type HeaderRow = {
  id: string;
  leftValue: ReactElement;
  rightValue: ReactElement;
};

const renderHeaderValueToPdf = (value: ReactElement): ReactElement => value;

type PdfHeaderTableProps = {
  quote: Quote;
  locale: string;
};

export const PdfHeaderTable = ({ quote, locale }: PdfHeaderTableProps): ReactElement => {
  const logoImg = (
    <View style={{ alignItems: 'center' }}>
      <PdfHeader
        image={{
          src: '../carrara-logo.jpg',
          imgStyle: {
            width: '2.85in',
            height: '1in',
          },
        }}
      />
    </View>
  );

  const projectLocation = [
    `${quote.project.address.basicDisplay()}`,
    quote.project.address.city,
    quote.project.address.state,
  ]
    .filter((s) => s !== '' && s !== null)
    .join(', ');

  const infoRows: InfoRow[] = [
    {
      id: randomUUID(),
      value: quote.company !== null ? `CUSTOMER: ${quote.company.name}` : '',
      viewStyle: infoStyle,
    },
    {
      id: randomUUID(),
      value: `PROJECT: ${quote.project.name}`,
      viewStyle: infoStyle,
    },
    {
      id: randomUUID(),
      value: `PROJECT LOCATION: ${projectLocation}`,
      viewStyle: infoStyle,
    },
    {
      id: randomUUID(),
      value: quote.creationDate === null ? null : `DATE: ${quote.creationDateDisplay(locale)}`,
      viewStyle: infoStyle,
    },
    {
      id: randomUUID(),
      value: quote.revisionDate === null ? null : `REVISED: ${quote.revisionDateDisplay(locale)}`,
      viewStyle: infoStyle,
    },
  ];

  const infoCols: PdfColumnConfig<InfoRow>[] = [
    {
      id: 'value',
      label: '',
      type: 'string',
      isDisplayed: false,
    },
  ];

  const infoTable = <PdfTable columns={infoCols.map(pdfColumnFromConfig)} rows={infoRows} />;

  const tableRows: HeaderRow[] = [
    {
      id: randomUUID(),
      leftValue: logoImg,
      rightValue: infoTable,
    },
  ];

  const tableCols: PdfColumnConfig<HeaderRow>[] = [
    {
      id: 'leftValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      formatValueForPdf: renderHeaderValueToPdf as (
        value: ReactElement | string | null,
      ) => ReactElement,
    },
    {
      id: 'rightValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      formatValueForPdf: renderHeaderValueToPdf as (
        value: ReactElement | string | null,
      ) => ReactElement,
    },
  ];

  return (
    <PdfTable columns={tableCols.map(pdfColumnFromConfig)} rows={tableRows} cellPadding='tight' />
  );
};
